<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Copy, Pencil, Plus, RefreshCw, Trash2, Wrench } from 'lucide-vue-next'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import PaginationControls from '../../components/PaginationControls.vue'
import { createReferenceItem, deleteReferenceItem, listEquipmentItems, updateReferenceItem } from '../productService'
import type { EquipmentReference } from '../types'

const client = useApiClient()
const auth = useAuthStore()
const { t } = useI18n()
const equipment = ref<EquipmentReference[]>([])
const loading = ref(false)
const saving = ref(false)
const errorMessage = ref('')
const formError = ref('')
const search = ref('')
const editingId = ref<number | null>(null)
const editorOpen = ref(false)
const currentPage = ref(1)
const pageSize = ref(10)
/** Sort order is no longer edited in the UI; edits keep the stored value. */
const editingSortOrder = ref(0)
const form = reactive({ code: '', name: '', machine_type: '', brand_name: '', model_name: '', needle_count: '', location: '', is_active: '1' })

/** Industry-standard sock/hosiery machine categories. */
const MACHINE_TYPE_CODES = [
  'single_cylinder',
  'double_cylinder',
  'computerized_single_cylinder',
  'computerized_double_cylinder',
  'linked_toe',
  'seamless',
  'flat_knitting',
  'hosiery',
  'other',
] as const
const machineTypeOptions = computed(() =>
  MACHINE_TYPE_CODES.map((code) => ({
    value: code,
    label: t(`engineering.equipment.machineTypeOptions.${code}`),
  })),
)
function machineTypeLabel(value?: string | null): string {
  const code = (value ?? '').trim()
  if (!code) return '—'
  return MACHINE_TYPE_CODES.includes(code as (typeof MACHINE_TYPE_CODES)[number])
    ? t(`engineering.equipment.machineTypeOptions.${code}`)
    : code
}
const canUpdate = computed(() => auth.user?.permissions.includes('products.update') ?? false)
const canDelete = computed(() => auth.user?.permissions.includes('products.delete') ?? false)
const filtered = computed(() => {
  const query = search.value.trim().toLocaleLowerCase()
  return equipment.value.filter((item) => !query || `${item.code} ${item.name} ${item.machine_type ?? ''} ${item.brand_name ?? ''} ${item.model_name ?? ''} ${item.location ?? ''}`.toLocaleLowerCase().includes(query))
})
const paginated = computed(() => filtered.value.slice((currentPage.value - 1) * pageSize.value, currentPage.value * pageSize.value))

function resetForm() {
  editingId.value = null
  editingSortOrder.value = 0
  Object.assign(form, { code: '', name: '', machine_type: '', brand_name: '', model_name: '', needle_count: '', location: '', is_active: '1' })
  formError.value = ''
}

function startCreate() {
  resetForm()
  editorOpen.value = true
}

function startEdit(item: EquipmentReference) {
  resetForm()
  editingId.value = item.id
  editingSortOrder.value = item.sort_order ?? 0
  Object.assign(form, {
    code: item.code,
    name: item.name,
    machine_type: item.machine_type ?? '',
    brand_name: item.brand_name ?? '',
    model_name: item.model_name ?? item.name,
    needle_count: String(item.needle_count ?? item.base_amount ?? ''),
    location: item.location ?? '',
    is_active: item.is_active ? '1' : '0',
  })
  editorOpen.value = true
}

function closeEditor() {
  editorOpen.value = false
  resetForm()
}

/** Next free `<code>-COPY`, `<code>-COPY2`, ... so the duplicate keeps a unique code. */
function duplicateCode(baseCode: string): string {
  const existing = new Set(equipment.value.map((item) => item.code.trim().toUpperCase()))
  const base = `${baseCode.trim().toUpperCase()}-COPY`
  if (!existing.has(base)) return base
  let counter = 2
  while (existing.has(`${base}${counter}`)) counter += 1
  return `${base}${counter}`
}

function startDuplicate(item: EquipmentReference) {
  startEdit(item)
  // Keep the specs, drop the identity: the copy is a new record with its own code.
  editingId.value = null
  editingSortOrder.value = 0
  form.code = duplicateCode(item.code)
  form.name = `${item.name} (copy)`
  formError.value = ''
}

async function loadEquipment() {
  loading.value = true
  errorMessage.value = ''
  try {
    equipment.value = await listEquipmentItems(client)
  } catch (error: unknown) {
    errorMessage.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

async function submit() {
  if (!canUpdate.value) return
  saving.value = true
  formError.value = ''
  const normalizedCode = form.code.trim().toUpperCase()
  if (equipment.value.some((item) => item.id !== editingId.value && item.code.trim().toUpperCase() === normalizedCode)) {
    formError.value = t('engineering.equipment.duplicateCode')
    saving.value = false
    return
  }
  const needleCount = form.needle_count === '' ? null : Number(form.needle_count)
  if (needleCount !== null && (!Number.isInteger(needleCount) || needleCount < 0)) {
    formError.value = t('engineering.equipment.invalidNeedleCount')
    saving.value = false
    return
  }
  const payload = {
    code: normalizedCode,
    name: form.name.trim(),
    machine_type: form.machine_type.trim() || null,
    brand_name: form.brand_name.trim() || null,
    model_name: form.model_name.trim() || null,
    needle_count: needleCount,
    location: form.location.trim() || null,
    sort_order: editingSortOrder.value,
    is_active: Number(form.is_active),
  }
  try {
    if (editingId.value == null) await createReferenceItem(client, 'equipment', payload)
    else await updateReferenceItem(client, 'equipment', editingId.value, payload)
    closeEditor()
    currentPage.value = 1
    await loadEquipment()
  } catch (error: unknown) {
    formError.value = formatApiError(error)
  } finally {
    saving.value = false
  }
}

async function remove(item: EquipmentReference) {
  if (!canDelete.value || !window.confirm(t('engineering.equipment.confirmDelete', { name: item.name }))) return
  try {
    await deleteReferenceItem(client, 'equipment', item.id)
    if (editingId.value === item.id) closeEditor()
    await loadEquipment()
  } catch (error: unknown) {
    errorMessage.value = formatApiError(error)
  }
}

onMounted(loadEquipment)
</script>

<template>
  <div class="page-heading">
    <div><p class="eyebrow">{{ t('engineering.equipment.eyebrow') }}</p><h2>{{ t('engineering.equipment.title') }}</h2><p>{{ t('engineering.equipment.description') }}</p></div>
    <div class="heading-actions">
      <div class="summary"><Wrench :size="18" /><strong>{{ equipment.length }}</strong><span>{{ t('engineering.equipment.count') }}</span></div>
      <button v-if="canUpdate" class="primary" type="button" @click="startCreate"><Plus :size="16" />{{ t('engineering.equipment.create') }}</button>
    </div>
  </div>
  <section class="card list-card">
    <div class="toolbar">
      <label><span>{{ t('engineering.equipment.search') }}</span><input v-model="search" type="search" :placeholder="t('engineering.equipment.searchPlaceholder')" @input="currentPage = 1"></label>
      <button type="button" :disabled="loading" @click="loadEquipment"><RefreshCw :size="16" />{{ t('engineering.action.retry') }}</button>
    </div>
    <div v-if="errorMessage" class="message error" role="alert">{{ errorMessage }}</div>
    <div v-if="loading" class="state">{{ t('engineering.equipment.loading') }}</div>
    <div v-else-if="!filtered.length" class="state">{{ t('engineering.equipment.empty') }}</div>
    <div v-else class="table-scroll">
      <table class="reference-table">
        <thead>
          <tr>
            <th>{{ t('engineering.equipment.code') }}</th>
            <th>{{ t('engineering.equipment.name') }}</th>
            <th>{{ t('engineering.equipment.machineType') }}</th>
            <th>{{ t('engineering.equipment.brandName') }}</th>
            <th>{{ t('engineering.equipment.modelName') }}</th>
            <th class="num">{{ t('engineering.equipment.needleCount') }}</th>
            <th>{{ t('engineering.equipment.location') }}</th>
            <th>{{ t('engineering.equipment.status') }}</th>
            <th><span class="visually-hidden">{{ t('engineering.catalog.actions') }}</span></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in paginated" :key="item.id">
            <td><strong>{{ item.code }}</strong></td>
            <td>{{ item.name }}</td>
            <td>{{ machineTypeLabel(item.machine_type) }}</td>
            <td>{{ item.brand_name || '—' }}</td>
            <td>{{ item.model_name || '—' }}</td>
            <td class="num">{{ item.needle_count ?? '—' }}</td>
            <td>{{ item.location || '—' }}</td>
            <td>{{ item.is_active ? t('engineering.equipment.active') : t('engineering.equipment.inactive') }}</td>
            <td class="actions">
              <button type="button" :disabled="!canUpdate" @click="startEdit(item)"><Pencil :size="15" />{{ t('engineering.equipment.edit') }}</button>
              <button type="button" :disabled="!canUpdate" @click="startDuplicate(item)"><Copy :size="15" />{{ t('engineering.equipment.duplicate') }}</button>
              <button class="danger" type="button" :disabled="!canDelete" @click="remove(item)"><Trash2 :size="15" />{{ t('engineering.equipment.remove') }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <PaginationControls v-if="!loading && filtered.length" v-model:page="currentPage" v-model:page-size="pageSize" :total="filtered.length" :page-size-options="[10,25,50]" />
  </section>

  <div v-if="editorOpen" class="offcanvas-backdrop" @click="closeEditor"></div>
  <aside v-if="editorOpen" class="offcanvas" role="dialog" aria-modal="true" :aria-label="editingId == null ? t('engineering.equipment.createTitle') : t('engineering.equipment.editTitle')">
    <header class="offcanvas-head">
      <div>
        <p class="eyebrow">{{ t('engineering.equipment.eyebrow') }}</p>
        <h3>{{ editingId == null ? t('engineering.equipment.createTitle') : t('engineering.equipment.editTitle') }}</h3>
      </div>
      <button class="icon-button" type="button" :aria-label="t('engineering.action.cancel')" :title="t('engineering.action.cancel')" @click="closeEditor">×</button>
    </header>
    <form class="offcanvas-body" @submit.prevent="submit">
      <p v-if="formError" class="message error" role="alert">{{ formError }}</p>
      <p v-if="!canUpdate" class="message hint">{{ t('engineering.equipment.permissionHint') }}</p>
      <label><span>{{ t('engineering.equipment.name') }} *</span><input v-model="form.name" :disabled="saving || !canUpdate" required maxlength="120"></label>
      <label><span>{{ t('engineering.equipment.code') }} *</span><input v-model="form.code" :disabled="saving || !canUpdate" required maxlength="40"></label>
      <label>
        <span>{{ t('engineering.equipment.machineType') }}</span>
        <select v-model="form.machine_type" :disabled="saving || !canUpdate">
          <option value="">{{ t('engineering.equipment.machineTypeUnset') }}</option>
          <option v-if="form.machine_type && !MACHINE_TYPE_CODES.includes(form.machine_type as (typeof MACHINE_TYPE_CODES)[number])" :value="form.machine_type">{{ form.machine_type }}</option>
          <option v-for="option in machineTypeOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
        </select>
      </label>
      <label><span>{{ t('engineering.equipment.brandName') }}</span><input v-model="form.brand_name" :disabled="saving || !canUpdate" maxlength="120"></label>
      <label><span>{{ t('engineering.equipment.modelName') }}</span><input v-model="form.model_name" :disabled="saving || !canUpdate" maxlength="120"></label>
      <label><span>{{ t('engineering.equipment.needleCount') }}</span><input v-model="form.needle_count" :disabled="saving || !canUpdate" min="0" step="1" type="number"></label>
      <label><span>{{ t('engineering.equipment.location') }}</span><input v-model="form.location" :disabled="saving || !canUpdate" maxlength="160"></label>
      <label><span>{{ t('engineering.equipment.status') }}</span><select v-model="form.is_active" :disabled="saving || !canUpdate"><option value="1">{{ t('engineering.equipment.active') }}</option><option value="0">{{ t('engineering.equipment.inactive') }}</option></select></label>
      <div class="offcanvas-actions">
        <button class="secondary-button" type="button" :disabled="saving" @click="closeEditor">{{ t('engineering.action.cancel') }}</button>
        <button class="primary" type="submit" :disabled="saving || !canUpdate || !form.name.trim() || !form.code.trim()">
          <Plus v-if="editingId == null" :size="16" /><Pencil v-else :size="16" />{{ saving ? t('engineering.equipment.saving') : t(editingId == null ? 'engineering.equipment.create' : 'engineering.equipment.saveChanges') }}
        </button>
      </div>
    </form>
  </aside>
</template>

<style scoped>
.page-heading,.toolbar,.actions,.summary,.heading-actions,.offcanvas-actions{display:flex;gap:12px;align-items:center;justify-content:space-between}
.page-heading{margin-bottom:24px}
.page-heading>div:first-child p:last-child{margin-top:8px;color:#66726e}
.eyebrow{margin:0 0 4px;color:#66726e;font-size:12px;font-weight:700;text-transform:uppercase}
h2,h3,p{margin:0}
.heading-actions{justify-content:flex-end;flex-wrap:wrap;gap:10px}
.summary{justify-content:flex-start;padding:12px 14px;border:1px solid #d7dfdc;border-radius:8px;background:#fff}
.summary span{color:#66726e;font-size:12px}
.card{min-width:0;padding:18px;border:1px solid #d7dfdc;border-radius:8px;background:#fff}
label{display:grid;gap:6px}
label span,th{color:#586660;font-size:12px;font-weight:700;text-transform:uppercase}
input,select,button{min-height:40px;padding:8px 10px;border:1px solid #adb9b4;border-radius:8px;background:#fff;font:inherit}
button{display:inline-flex;gap:7px;align-items:center;justify-content:center;font-weight:700;cursor:pointer}
.primary{border:0;color:#fff;background:#3568a8}
.secondary-button{color:#285b9a;background:#fff}
.icon-button{width:40px;min-height:40px;padding:0;border:1px solid #d7dfdc;color:#53625d;background:#fff;font-size:20px;font-weight:700;line-height:1}
.toolbar{justify-content:flex-start}
.toolbar label{flex:1;max-width:420px}
.toolbar input{width:100%}
.message{padding:12px;border-radius:8px}
.error{color:#852d22;background:#fff5f3}
.hint{color:#66726e;background:#f3f6f5}
.state{display:grid;min-height:220px;color:#697671;place-items:center}
.table-scroll{overflow-x:auto}
table{width:100%;min-width:900px;margin-top:16px;border-collapse:collapse}
th,td{padding:10px 12px;border-bottom:1px solid #e1e6e4;text-align:left;white-space:nowrap}
th{background:#f3f6f5}
th.num,td.num{text-align:right;font-variant-numeric:tabular-nums}
.actions{justify-content:flex-start}
.danger{color:#8b3025}
.visually-hidden{position:absolute;width:1px;height:1px;overflow:hidden;clip-path:inset(50%);white-space:nowrap}
button:disabled{cursor:not-allowed;opacity:.55}
.offcanvas-backdrop{position:fixed;z-index:60;inset:0;background:rgb(15 30 26 / 35%)}
.offcanvas{position:fixed;z-index:61;top:0;right:0;display:flex;width:min(480px,100vw);height:100%;flex-direction:column;background:#fff;box-shadow:-12px 0 40px rgb(15 30 26 / 20%)}
.offcanvas-head{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;padding:20px;border-bottom:1px solid #e2e6e3}
.offcanvas-head h3{font-size:18px}
.offcanvas-body{display:grid;align-content:start;gap:14px;padding:20px;overflow-y:auto}
.offcanvas-actions{justify-content:flex-end;gap:10px;margin-top:4px;padding-top:14px;border-top:1px solid #eef2f0}
@media(max-width:640px){.page-heading,.heading-actions,.toolbar,.actions{align-items:stretch;flex-direction:column}.offcanvas{width:100vw}}
</style>
